import styles from './RefineBar.less';

import React, { Component, PropTypes } from 'react';
import { connect } from 'react-redux';
import { ChevronIcon } from 'seek-style-guide/react';
import classnames from 'classnames';

import { updateActiveRefinement, clearActiveRefinement, selectRefinement } from 'store/search/search';

import { ScreenReaderOnly } from 'seek-style-guide/react';
import WorkTypeSummary from 'WorkTypeSummary/WorkTypeSummary';
import WorkTypes from 'WorkTypes/WorkTypes';
import DateListed from 'DateListed/DateListed';
import Salary from 'Salary/Salary';

function mapStateToProps({ search }) {
  return {
    refinements: search.refinements,
    activeRefinement: search.activeRefinement
  };
}

function mapDispatchToProps(dispatch) {
  return {
    openWorkType: () => dispatch(updateActiveRefinement('WorkType')),
    openSalaryFrom: () => dispatch(updateActiveRefinement('SalaryFrom')),
    openSalaryTo: () => dispatch(updateActiveRefinement('SalaryTo')),
    openDateListed: () => dispatch(updateActiveRefinement('DateListed')),
    selectRefinement: (type, refinement) => dispatch(selectRefinement(type, refinement)),
    closeRefinements: () => dispatch(clearActiveRefinement())
  };
}

@connect(mapStateToProps, mapDispatchToProps)
class RefineBar extends Component {

  static propTypes = {
    refinements: PropTypes.shape({
      worktype: PropTypes.array.isRequired,
      salary: PropTypes.object.isRequired
    }).isRequired,
    activeRefinement: PropTypes.string,
    openWorkType: PropTypes.func.isRequired,
    openSalaryFrom: PropTypes.func.isRequired,
    openSalaryTo: PropTypes.func.isRequired,
    openDateListed: PropTypes.func.isRequired,
    selectRefinement: PropTypes.func.isRequired,
    closeRefinements: PropTypes.func.isRequired,
    expanded: PropTypes.bool.isRequired
  };

  constructor() {
    super();

    this.handleWorkTypeSelection = this.handleSelection.bind(this, 'WorkType');
    this.handleSalaryFromSelection = this.handleSelection.bind(this, 'SalaryFrom');
    this.handleSalaryToSelection = this.handleSelection.bind(this, 'SalaryTo');
    this.handleDateListedSelection = this.handleSelection.bind(this, 'DateListed');
    this.storeCloseRefinementsReference = this.storeCloseRefinementsReference.bind(this);
  }

  storeCloseRefinementsReference(input) {
    if (input !== null) {
      this.closeRefinements = input;
    }
  }

  handleSelection(type, refinement) {
    // outerWidth was returning 0 on Safari
    const screenWidth = Math.max(window.outerWidth, window.innerWidth);
    if (type !== 'WorkType' && screenWidth < 650) {
      this.closeRefinements.click();
    }

    this.props.selectRefinement(type, refinement);
  }

  render() {
    const { refinements, activeRefinement, openWorkType, openSalaryFrom,
            openSalaryTo, openDateListed, closeRefinements, expanded } = this.props;

    const defaultSalaryTypeLabel = 'Annually';
    const selectedSalaryType = refinements.salary.types.filter(item => item.isActive)[0] || {
      label: defaultSalaryTypeLabel
    };
    const selectedSalaryFrom = refinements.salary.from.filter(item => item.isActive)[0] || {
      label: '$0'
    };
    const selectedSalaryTo = refinements.salary.to.filter(item => item.isActive)[0] || {
      label: selectedSalaryType.label === defaultSalaryTypeLabel ? '$200k+' : '$100+'
    };
    const selectedDateListed = refinements.dateRange.filter(item => item.isActive)[0] || {
      label: 'Any time',
      value: '999'
    };
    const refineDateListedLabel = (selectedDateListed.value === '999' || selectedDateListed.value === '1') ?
                                    'listed' : 'listed in';
    const rootClasses = classnames({
      [styles.root]: true,
      [styles.root_isExpanded]: expanded,
      [styles.root_isRefining]: ['WorkType', 'SalaryFrom', 'SalaryTo', 'DateListed'].indexOf(activeRefinement) > -1
    });

    return (
      <aside role="complementary" aria-hidden={!expanded} className={rootClasses}>
        <ScreenReaderOnly>
          <h1>Refine your search</h1>
        </ScreenReaderOnly>

        <input
          id="RefineBar--WorkType"
          className={`${styles.toggle} ${styles.toggle_isWorkType}`}
          name="RefineBar__Toggle"
          type="radio"
          checked={activeRefinement === 'WorkType'}
          onChange={openWorkType}
        />
        <input
          id="RefineBar--SalaryFrom"
          className={`${styles.toggle} ${styles.toggle_isSalaryFrom}`}
          name="RefineBar__Toggle"
          type="radio"
          checked={activeRefinement === 'SalaryFrom'}
          onChange={openSalaryFrom}
        />
        <input
          id="RefineBar--SalaryTo"
          className={`${styles.toggle} ${styles.toggle_isSalaryTo}`}
          name="RefineBar__Toggle"
          type="radio"
          checked={activeRefinement === 'SalaryTo'}
          onChange={openSalaryTo}
        />
        <input
          id="RefineBar--DateListed"
          className={`${styles.toggle} ${styles.toggle_isDateListed}`}
          name="RefineBar__Toggle"
          type="radio"
          checked={activeRefinement === 'DateListed'}
          onChange={openDateListed}
        />
        <input
          id="RefineBar--Done"
          className={styles.done}
          type="radio"
          name="RefineBar__Toggle"
          onChange={closeRefinements}
          ref={this.storeCloseRefinementsReference}
        />

        <label
          className={`${styles.summaryLabel} ${styles.summaryLabel_isDone}`}
          htmlFor="RefineBar--Done"
          onChange={closeRefinements}>
          <ScreenReaderOnly>Hide refinements</ScreenReaderOnly>
        </label>

        <p className={styles.summary}>
          <label
            className={`${styles.summaryLabel} ${styles.summaryLabel_isWorkType}`}
            htmlFor="RefineBar--WorkType"
            onClick={openWorkType}
            data-automation="toggleWorkTypePanel">
            <ScreenReaderOnly>Show work type refinements. </ScreenReaderOnly>
            <span className={styles.summaryLabelText}>
              <WorkTypeSummary workTypes={refinements.worktype} />
              <ChevronIcon direction="down" svgClassName={styles.chevronSvg} />
            </span>
            <span className={`${styles.summaryLabelBorder} ${styles.summaryLabelBorder_isLeft}`} />
            <span className={`${styles.summaryLabelBorder} ${styles.summaryLabelBorder_isRight}`} />
          </label>

          <label
            className={`${styles.summaryLabel} ${styles.summaryLabel_isSalaryFrom}`}
            htmlFor="RefineBar--SalaryFrom"
            onClick={openSalaryFrom}
            data-automation="toggleSalaryMinimumPanel">
            <ScreenReaderOnly>Show minimum salary refinements. </ScreenReaderOnly>
            <span className={styles.summaryLabelText}>
              <span className={styles.summarySentence} aria-hidden="true">paying </span>
              {selectedSalaryFrom.label}
              <ChevronIcon direction="down" svgClassName={styles.chevronSvg} />
            </span>
            <span className={`${styles.summaryLabelBorder} ${styles.summaryLabelBorder_isRight}`} />
          </label>

          <label
            className={`${styles.summaryLabel} ${styles.summaryLabel_isSalaryTo}`}
            htmlFor="RefineBar--SalaryTo"
            onClick={openSalaryTo}
            data-automation="toggleSalaryMaximumPanel">
            <ScreenReaderOnly>Show maximum salary refinements. </ScreenReaderOnly>
            <span className={styles.summaryLabelText}>
              <span className={styles.summarySentence} aria-hidden="true">to </span>
              {selectedSalaryTo.label}
              <ChevronIcon direction="down" svgClassName={styles.chevronSvg} />
            </span>
            <span className={`${styles.summaryLabelBorder} ${styles.summaryLabelBorder_isRight}`} />
          </label>

          <label
            className={`${styles.summaryLabel} ${styles.summaryLabel_isDateListed}`}
            htmlFor="RefineBar--DateListed"
            onClick={openDateListed}
            data-automation="toggleDateListedPanel">
            <ScreenReaderOnly>Show date listed refinements. </ScreenReaderOnly>
            <span className={styles.summaryLabelText}>
              <span className={styles.summarySentence} aria-hidden="true">{refineDateListedLabel}</span>
              {selectedDateListed.label.toLowerCase()}
              <ChevronIcon direction="down" svgClassName={styles.chevronSvg} />
            </span>
            <span className={`${styles.summaryLabelBorder} ${styles.summaryLabelBorder_isRight}`} />
          </label>

          <label
            className={styles.summaryDone}
            htmlFor="RefineBar--Done"
            onClick={closeRefinements}>
            <ScreenReaderOnly>Hide refinements</ScreenReaderOnly>
          </label>
        </p>

        <div className={`${styles.refinementPanel} ${styles.refinementPanel_isWorkType}`}>
          <WorkTypes
            active={activeRefinement === 'WorkType'}
            handleSelection={this.handleWorkTypeSelection}
            workTypes={refinements.worktype}
          />
        </div>

        <div className={`${styles.refinementPanel} ${styles.refinementPanel_isSalaryFrom}`}>
          <Salary
            active={activeRefinement === 'SalaryFrom'}
            handleSelection={this.handleSalaryFromSelection}
            salaries={refinements.salary.from}
            salaryTypes={refinements.salary.types}
            label="Minimum"
          />
        </div>

        <div className={`${styles.refinementPanel} ${styles.refinementPanel_isSalaryTo}`}>
          <Salary
            active={activeRefinement === 'SalaryTo'}
            handleSelection={this.handleSalaryToSelection}
            salaries={refinements.salary.to}
            salaryTypes={refinements.salary.types}
            label="Maximum"
          />
        </div>

        <div className={`${styles.refinementPanel} ${styles.refinementPanel_isDateListed}`}>
          <DateListed
            active={activeRefinement === 'DateListed'}
            handleSelection={this.handleDateListedSelection}
            dateRanges={refinements.dateRange}
          />
        </div>

        <div className={styles.backdrop} />
      </aside>
    );
  }
}

export default RefineBar;



/** WEBPACK FOOTER **
 ** ./app/components/Search/components/RefineBar/RefineBar.js
 **/